// Copyright (c) 2020-2024 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import { observable, computed, action, makeObservable } from 'mobx';
import { jwtDecode } from "jwt-decode";
import isEqual from 'lodash/isEqual';

import container from '@core/di';
import AuthService from '@core/services/auth';
import SystemUser, { SystemUserDTO } from '@shared/models/system-user';
import ProfileStore from '@shared/stores/profile';
import EntitiesTemplatesStore from './entities-templates';
import history from '@shared/utils/history';
import ROUTES from '@shared/constants/routes';
import { deserialize, serialize } from '@shared/utils/common';
// import { NotificationType, showNotification } from '@shared/components/Notification';
// import { CredentialResponse } from '@react-oauth/google';
import { TOKENS_KEYS } from '@shared/constants/common';
import { CURRENT_TAB_INDEX } from '@shared/constants/sp-sheets';
const LOGIN_REDIRECT_URL_KEY = 'vi_loginRedirectURL';
const PREVIOUS_USER_ROLES_KEY = 'vi_previousUserRoles';


export enum TokenRefreshStatus {
  refreshing,
  refreshed,
  initial,
}

// Use the methods exposed in the AuthService to authenticate a user login.
@injectable()
export default class AuthStore {
  static diToken = Symbol('auth-store');
  private service = container.get<AuthService>(AuthService.diToken);
  private entitiesTemplatesStore = container.get<EntitiesTemplatesStore>(EntitiesTemplatesStore.diToken);
  private profileStore: ProfileStore;
  @observable tokenRefreshStatus = TokenRefreshStatus.initial;
  @observable _user;

  constructor() {
    this._user = undefined;
    makeObservable(this);
  }

  // Return the current login user.
  @computed get user(): SystemUser {
    return this._user;
  }

  // Check if the user has successfully logged in.
  @computed get loggedIn(): boolean {
    return Boolean(this.user);
  }

  // Get the access and refresh tokens.
  get tokens() {
    return {
      refresh: sessionStorage.getItem(TOKENS_KEYS.refresh),
      access: sessionStorage.getItem(TOKENS_KEYS.access)
    };
  }

  // Get the URL to redirect to after logging in.
  get loginRedirectURL() {
    if (isEqual(this.user.roles, this.previousUserRoles)) {
      return localStorage.getItem(LOGIN_REDIRECT_URL_KEY);
    }

    return '';
  }

  // Set up the store object.
  initialize() {
    this.profileStore = container.get(ProfileStore.diToken);

    this.setUser();

    if (!this.loggedIn) {
      // Check if the current URL matches any private routes (such as /sheets, /map, /orders, etc.)
      const isPrivatePathname = Object.values(ROUTES.private).some((privatePath) => {
        return history.location.pathname.includes(privatePath);
      });

      console.log("********** in initialize:", isPrivatePathname, history.location.pathname);
      if (isPrivatePathname) {
        this.setLoginRedirectURL();
      }
    }
  }

  // Save redirect URL to local storage.
  private setLoginRedirectURL = () => {
    console.log("********** CHECK SetLoginRedirectURL:", history.location.pathname);
    localStorage.setItem(LOGIN_REDIRECT_URL_KEY, history.location.pathname);
  }

  // Setup user obj using data store in the current browser session.
  @action private async setUser() {
    // Read data from session storage.
    const token = sessionStorage.getItem(TOKENS_KEYS.access);
    if (!token) {
      console.log('Could not read access token.');
      // <Notification /> is not available at this point
      // showNotification("Could not read access token.", NotificationType.error);
      return;
    }

    // Setup the user obj.
    const data = jwtDecode<SystemUserDTO>(token);
    this._user = new SystemUser(data);

    //console.log("*** data/user:", data, this._user);

    // Get user name from portal-api.
    const { fullName, profilePhoto } = await this.profileStore.get();

    this.user.update({ fullName });
    this.user.update({ img: profilePhoto as string})
  }

  // Update user name.
  @action async updateUser() {
    const { fullName } = await this.profileStore.get();

    this._user.update({ fullName });
  }

  // Store the tokens in the browser current session.
  private setTokens(tokens: { accessToken: string; refreshToken: string }) {
    sessionStorage.setItem(TOKENS_KEYS.access, tokens.accessToken);
    sessionStorage.setItem(TOKENS_KEYS.refresh, tokens.refreshToken);
  }

  // Set flag to indicate this user logs on using MS.
  private setMicrosoft() {
    sessionStorage.setItem(TOKENS_KEYS.msLogin, '1');
  }

  // Store the tokens and setup authenticated user.
  private setAuthData(tokens: { accessToken: string; refreshToken: string }) {
    this.setTokens(tokens);
    this.setUser();
  }

  // Authenticate user login.
  async login(data: { username: string; password: string; twofa: string }) {
    const authData = await this.service.login(data);
    //console.log("*** check AuthData:", data, authData);

    // After login, set tab to Summary to allow setting filters to reduce site count before clicking on on Status tab.
    localStorage.setItem(CURRENT_TAB_INDEX, '0');

    // Log in with Microsoft (?)
    if(data.username.toLowerCase() === 'microsoft@vi-portal.net') {
      this.setMicrosoft();
    } 

    this.setAuthData(authData);
  }

  async sendMSLogoutRequest() {
    console.log("****** in store SendLogoutRequest");

    const data = await this.service.sendMSLogoutRequest();
    return data;
  }

  // Re-authenticate user login (?)
  @action async refreshToken() {
    // If the token is being refreshed, do nothing to avoid multiple refresh requests on the same token.
    if (this.tokenRefreshStatus === TokenRefreshStatus.refreshing) {
      return;
    }

    // logout if there is no refresh token (?)
    if (!this.tokens.refresh) {
      this.logout();

      return;
    }

    // Set the status to refreshing to prevent multiple refresh requests.
    this.tokenRefreshStatus = TokenRefreshStatus.refreshing;

    try {
      // Get a new token and set the auth header
      const authData = await this.service.refreshToken(this.tokens.refresh);

      this.setAuthData(authData);

      this.tokenRefreshStatus = TokenRefreshStatus.refreshed;
    } 
    catch {
      console.log("****** Exception in refreshToken().  Force a logout.");
      this.logout();
    }
  }

  // Store roles of the current login user.
  private setPreviousUserRoles = () => {
    localStorage.setItem(PREVIOUS_USER_ROLES_KEY, serialize(this.user.roles));
  }

  // Load roles stored from last time.
  private get previousUserRoles() {
    return deserialize(localStorage.getItem(PREVIOUS_USER_ROLES_KEY));
  }

  // Log out the current user.
  logout = (clearLoginRedirectURL?: boolean) => {
    if (!clearLoginRedirectURL) {
      this.setLoginRedirectURL();
    }

    this.setPreviousUserRoles();
    this.reset({ loginRedirectURL: clearLoginRedirectURL }); // Reset to complete logging out.
  }

  // Clear redirect URL from local storage.
  private resetLoginRedirectURL = () => {
    localStorage.setItem(LOGIN_REDIRECT_URL_KEY, '');
  }

  // Clear tokens and other authentication info.
  @action private reset(config?: { loginRedirectURL?: boolean }) {
    // These probably are not needed.
    sessionStorage.setItem(TOKENS_KEYS.access, '');
    sessionStorage.setItem(TOKENS_KEYS.refresh, '');
    
    this.tokenRefreshStatus = TokenRefreshStatus.initial;
    this._user = undefined;

    this.entitiesTemplatesStore.reset();

    if (config?.loginRedirectURL) {
      this.resetLoginRedirectURL();
    }
  }
}